import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { IS_ARCHIVE, LIVE_SITE_URL } from "@/lib/archive";
// Turnstile disabled — rate limiting is sufficient for now. Re-enable if spam becomes an issue.
// import { TurnstileWidget, resetTurnstile } from "@/components/common/TurnstileWidget";

const INTEREST_OPTIONS = [
  { value: "", label: "Select a topic..." },
  { value: "xeni", label: "XENI (immigration law)" },
  { value: "robolab-x", label: "Robolab X (education)" },
  { value: "metaroom", label: "Metaroom (investment management)" },
  { value: "partnership", label: "Partnership opportunities" },
  { value: "platform", label: "Platform and security questions" },
  { value: "careers", label: "Careers" },
  { value: "other", label: "Something else" },
];

const INPUT_BASE =
  "w-full px-4 py-3 border bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-sm placeholder:text-[var(--color-fg-tertiary)] focus:outline-none transition-colors";

const INPUT_OK = `${INPUT_BASE} border-[var(--color-border)] focus:border-[var(--color-fg-secondary)]`;
const INPUT_ERR = `${INPUT_BASE} border-red-500/60 focus:border-red-500`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Errors = Partial<Record<"name" | "email" | "interest" | "message", string>>;

function validate(form: { name: string; email: string; interest: string; message: string }): Errors {
  const errors: Errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.email.trim()) errors.email = "Email is required";
  else if (!EMAIL_RE.test(form.email)) errors.email = "Enter a valid email address";
  if (!form.interest) errors.interest = "Please select a topic";
  if (!form.message.trim()) errors.message = "Message is required";
  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="text-[11px] font-[family-name:var(--font-mono)] text-red-500 mt-1.5">
      {message}
    </p>
  );
}

export function ContactForm() {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("interest") ?? "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    interest: INTEREST_OPTIONS.some((o) => o.value === preselect) ? preselect : "",
    message: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [archived, setArchived] = useState(false);
  // const [turnstileToken, setTurnstileToken] = useState("");

  const handleChange = useCallback(
    (field: keyof typeof form) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => {
          if (!prev[field as keyof Errors]) return prev;
          const next = { ...prev };
          delete next[field as keyof Errors];
          return next;
        });
      },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const errs = validate(form);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return;
      }

      // v1 archive has no /api/contact backend — show a notice instead of POSTing.
      if (IS_ARCHIVE) {
        setArchived(true);
        return;
      }

      setSubmitting(true);
      setSubmitError("");

      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, turnstileToken: "" }),
        });

        const data = await res.json() as { success?: boolean; error?: string };

        if (res.ok && data.success) {
          setSubmitted(true);
        } else {
          setSubmitError(data.error ?? "Something went wrong. Please try again.");
        }
      } catch {
        setSubmitError("Network error. Please try again.");
      } finally {
        setSubmitting(false);
        // resetTurnstile();
      }
    },
    [form],
  );

  if (submitted) {
    return (
      <div className="border border-[var(--color-border)] p-8 md:p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
        <div className="text-2xl mb-4 font-[family-name:var(--font-display)] font-light">
          Message sent
        </div>
        <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed max-w-sm mb-6">
          Thanks for reaching out. We've received your message and will get back to you within one business day.
        </p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", organisation: "", interest: "", message: "" }); }}
          className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] transition-colors cursor-pointer"
        >
          Send another message →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest mb-2">
            Name <span className="text-[var(--color-fg-tertiary)]">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange("name")}
            placeholder="Your name"
            className={errors.name ? INPUT_ERR : INPUT_OK}
          />
          <FieldError message={errors.name} />
        </div>
        <div>
          <label className="block text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest mb-2">
            Email <span className="text-[var(--color-fg-tertiary)]">*</span>
          </label>
          <input
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            placeholder="you@company.com"
            className={errors.email ? INPUT_ERR : INPUT_OK}
          />
          <FieldError message={errors.email} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest mb-2">
          Organisation
        </label>
        <input
          type="text"
          value={form.organisation}
          onChange={handleChange("organisation")}
          placeholder="Your company or firm"
          className={INPUT_OK}
        />
      </div>

      <div>
        <label className="block text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest mb-2">
          I'm interested in... <span className="text-[var(--color-fg-tertiary)]">*</span>
        </label>
        <select
          value={form.interest}
          onChange={handleChange("interest")}
          className={`${errors.interest ? INPUT_ERR : INPUT_OK} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><path fill="gray" d="M6 8L1 3h10z"/></svg>')] bg-[length:12px] bg-[right_16px_center] bg-no-repeat`}
        >
          {INTEREST_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.value === ""}>
              {opt.label}
            </option>
          ))}
        </select>
        <FieldError message={errors.interest} />
      </div>

      <div>
        <label className="block text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] uppercase tracking-widest mb-2">
          Message <span className="text-[var(--color-fg-tertiary)]">*</span>
        </label>
        <textarea
          rows={5}
          value={form.message}
          onChange={handleChange("message")}
          placeholder="Tell us about your business and what you're looking for"
          className={`${errors.message ? INPUT_ERR : INPUT_OK} resize-none`}
        />
        <FieldError message={errors.message} />
      </div>

      {submitError && (
        <p className="text-[11px] font-[family-name:var(--font-mono)] text-red-500">{submitError}</p>
      )}

      {archived && (
        <p className="text-xs font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
          This is an archived snapshot — the contact form is disabled here. To reach us,
          visit{" "}
          <a href={LIVE_SITE_URL} className="underline hover:text-[var(--color-fg)] transition-colors">
            cosx.co
          </a>{" "}
          or email hi@cosx.co.
        </p>
      )}

      <div className="flex items-center gap-4">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-6 py-3 rounded-full text-sm font-medium bg-[var(--color-fg)] text-[var(--color-bg)] hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {submitting ? "Sending..." : "Send message"}
        </button>
        <span className="text-xs text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">
          We typically respond within one business day.
        </span>
      </div>

      {/* <TurnstileWidget onToken={setTurnstileToken} /> */}
    </form>
  );
}
